import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Tooltip,
  StackProps,
} from "@chakra-ui/react";
import { QuestionOutlineIcon } from "@chakra-ui/icons";
import { FieldInput } from "./FieldInput";
import { useFieldValidation } from "../../hooks/useFieldValidation";

interface FieldPreviewProps {
  fieldType: string;
  fieldName: string;
  description?: string;

  // String attributes
  minLength?: number | "";
  maxLength?: number | "";
  regexPattern?: string;
  stringAllowedValues?: string;

  // Number attributes
  minValue?: number | string | "";
  maxValue?: number | string | "";
  decimalPlaces?: number | "";
  numberAllowedValues?: string;

  // Date attributes
  dateMinValue?: string;
  dateMaxValue?: string;

  // Time attributes
  timeMinValue?: string;
  timeMaxValue?: string;

  // Boolean attributes
  booleanDefaultValue?: boolean;

  // Layout props
  labelProps?: StackProps;
  valueProps?: StackProps;
}

export const FieldPreview: React.FC<FieldPreviewProps> = ({
  fieldType,
  fieldName,
  description,
  minLength,
  maxLength,
  regexPattern,
  stringAllowedValues,
  minValue,
  maxValue,
  decimalPlaces,
  numberAllowedValues,
  dateMinValue,
  dateMaxValue,
  timeMinValue,
  timeMaxValue,
  booleanDefaultValue,
  labelProps,
  valueProps,
}) => {
  const [testValue, setTestValue] = useState<any>("");
  const [multiSelectValue, setMultiSelectValue] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const isMultiSelect =
    fieldType === "TextMultiSelection" || fieldType === "NumberMultiSelection";

  useEffect(() => {
    if (fieldType === "Toggle") {
      setTestValue(booleanDefaultValue || false);
    } else if (isMultiSelect) {
      setMultiSelectValue([]);
      setTestValue("");
    } else {
      setTestValue("");
    }
  }, [fieldType, booleanDefaultValue, isMultiSelect]);

  const validationError = useFieldValidation({
    fieldType,
    value: testValue,
    minLength,
    maxLength,
    regexPattern,
    minValue,
    maxValue,
    decimalPlaces,
    timeMinValue,
    timeMaxValue,
  });

  return (
    <FormControl
      isInvalid={!!validationError}
      display="flex"
      flexDir="row"
      w="100%"
    >
      <VStack w="100%" alignItems="flex-start" flex={1} pt={2} {...labelProps}>
        <FormLabel fontWeight="bold" color="cherryGrey.900">
          <HStack spacing={1}>
            <Text>{fieldName || "Field Name"}</Text>
            {description && (
              <Tooltip label={description} fontSize="xs" placement="top">
                <QuestionOutlineIcon boxSize={3} color="gray.500" />
              </Tooltip>
            )}
          </HStack>
        </FormLabel>
      </VStack>
      <VStack
        w="100%"
        alignItems="flex-start"
        alignSelf="center"
        flex={3}
        {...valueProps}
      >
        <FieldInput
          fieldType={fieldType}
          value={testValue}
          onChange={setTestValue}
          stringAllowedValues={stringAllowedValues}
          decimalPlaces={decimalPlaces}
          numberAllowedValues={numberAllowedValues}
          dateMinValue={dateMinValue}
          dateMaxValue={dateMaxValue}
          timeMinValue={timeMinValue}
          timeMaxValue={timeMaxValue}
          multiSelectValue={multiSelectValue}
          onMultiSelectChange={setMultiSelectValue}
          size="sm"
          bg="white"
        />

        {validationError && (
          <FormErrorMessage fontSize="xs">{validationError}</FormErrorMessage>
        )}

        {!validationError && testValue && testValue !== "" && (
          <FormHelperText fontSize="xs" color="green.600">
            ✓ Valid value
          </FormHelperText>
        )}
      </VStack>
    </FormControl>
  );
};
